import { Box } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProjectRead } from "@nens/rana/api-v1";
import { RanaModal } from "@nens/ranalib";
import { useLastTruthyValue, useOnlyOnce, useRect, useUpdate } from "@nens/react/hooks";

import { BackButton } from "@/components/back-button";
import { TenantProjectListTable, useTenantProjectListData } from "@/features/project";
import { useProjectPermissionsQueries } from "@/queries/query-project-permission";
import { ProjectContext } from "@/stores/project-provider";

import { OnImportFromCatalogueFunc } from "../types/on-import";

import { SchematisationProjectImportModalFromCatalogueDirectory } from "./schematisation-project-import-modal-from-catalogue-directory";

export function SchematisationProjectImportModalFromCatalogue({
  onClose,
  isMutating,
  onImport,
}: {
  onClose: () => void;
  isMutating: boolean;
  onImport: OnImportFromCatalogueFunc;
}) {
  // Save the size of the project table so that when we switch to the directory view, it doesn't jump around in size.
  const projectTableRef = useRef<HTMLDivElement | null>(null);
  const projectTableRect = useRect(projectTableRef);
  const width = useLastTruthyValue(projectTableRect?.width);
  const height = useLastTruthyValue(projectTableRect?.height);

  const [selectedProject, setSelectedProject] = useState<ProjectRead | null>(null);
  const intl = useIntl();
  const projectList = useTenantProjectListData();
  const projectPermissionQueries = useProjectPermissionsQueries(
    projectList.projectsOnPage.map((project) => project.id)
  );
  const selectedProjectPermissionsQuery = projectPermissionQueries.find(
    (_, index) => projectList.projectsOnPage[index]?.id === selectedProject?.id
  );
  const writableProjects = projectList.projectsOnPage.filter(
    (_, index) => projectPermissionQueries[index]?.data?.project_write === true
  );

  const pathRef = useRef("");

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={<FormattedMessage id="heading.select_project" defaultMessage="Select project" />}
      buttons={[
        {
          children: intl.formatMessage({ id: "button.cancel", defaultMessage: "Cancel" }),
          variant: "tertiary",
          onClick: onClose,
        },
        {
          children: intl.formatMessage({
            id: "button.add_to_project",
            defaultMessage: "Add to project",
          }),
          variant: "primary",
          isDisabled:
            !selectedProject ||
            selectedProjectPermissionsQuery?.isLoading ||
            selectedProjectPermissionsQuery?.data?.project_write !== true ||
            isMutating,
          onClick: selectedProject
            ? () => onImport({ project: selectedProject, path: pathRef.current })
            : undefined,
        },
      ]}
    >
      {selectedProject ? (
        <Box w={width} h={height}>
          <BackButton back={() => setSelectedProject(null)}>
            <FormattedMessage id="button.back_to_projects" defaultMessage="Back to projects" />
          </BackButton>
          <ProjectContext.Provider value={selectedProject}>
            <SchematisationProjectImportModalFromCatalogueDirectory
              passUpPath={(path) => (pathRef.current = path)} // Little on the hacky side, but by far the easiest solution since the child component needs the projectContext
            />
          </ProjectContext.Provider>
        </Box>
      ) : (
        <Box ref={projectTableRef}>
          <TenantProjectListTable
            {...projectList}
            projectsOnPage={writableProjects}
            getRowProps={(project) => ({
              onClick: () => setSelectedProject(project),
              cursor: "pointer",
            })}
          />
        </Box>
      )}
    </RanaModal>
  );
}
